import React from "react";
import { Link } from "react-router-dom";

import { TextField, Button, FunctionField } from "react-admin";

import {
  Card,
  CardContent,
  Grid,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { orange } from "@material-ui/core/colors";

import { isAuthorized } from "../../../utils/appUtils";
import { AclType, CarrierType } from "../../../utils/constants";
import { isBrokeredAccount, isInternalAccount } from "../accounts/AccountUtils";
import { getTrackCarLocationDisplayLabel } from "../../../utils/locationUtils";
import { formatCurrency, getFormTab } from "../../../utils/utils";

import {
  TrainIcon,
  ExceptionIcon,
  ReturnIcon,
} from "../../../components/common/iconography";
import { PhotoViewer } from "../../../components/common/PhotoViewer";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";
import ShipsTogetherAside from "../../../components/common/shipWith/ShipsTogetherAside";

import { getTotalInvoiced } from "../invoices/InvoiceUtils";
import { statusRenderer } from "./OutboundUtils";

const useAsideStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "18em",
      minWidth: "18em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  exception: {
    marginTop: theme.spacing(0.5),
  },
}));

export const OutboundAside = ({
  backPath,
  history,
  record,
  photos,
  ...props
}) => {
  const classes = useAsideStyles();
  var tab = getFormTab(props);
  const linkBackPath = backPath
    ? [...backPath, props.location.pathname]
    : [props.location.pathname];
  const invoiceLinkId = record?.invoice?.id || (record?.invoiceId || "").trim();

  const handleTrackClick = () => {
    history.push({
      pathname: "/trackManagementAdmin",
      state: { backPath: backPath, railCarNumber: record.railCarNumber },
    });
  };

  if (!record || tab === "1" || tab === "pickList") return null;
  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid container direction="column" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Status:
            </Typography>
            <br />
            <FunctionField
              source="status"
              record={record}
              render={statusRenderer}
            />
          </Grid>
          {record.returnShipment && (
            <Grid container direction="row">
              <Grid item xs={2}>
                <Tooltip
                  title={record?.returnReason ?? "Return Shipment"}
                  placement="left"
                  arrow
                >
                  <ReturnIcon style={{ color: orange[600] }} />
                </Tooltip>
              </Grid>
              <Grid item xs={10} className={classes.exception}>
                <Typography variant="body2">Return Shipment</Typography>
              </Grid>
            </Grid>
          )}
          {record.assignedTo && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Assigned To:
              </Typography>
              <br />
              <TextField source="assignedTo" record={record} />
            </Grid>
          )}
          {record.hasException && (
            <>
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Exceptions
                </Typography>
              </Grid>
              <Grid container direction="row">
                <Grid item xs={2}>
                  <Tooltip title={"Barcode Mismatch"} placement="left" arrow>
                    <ExceptionIcon style={{ color: orange[600] }} />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} className={classes.exception}>
                  <Typography variant="body2">Barcode Mismatch</Typography>
                </Grid>
              </Grid>
            </>
          )}
          {record.trackCar && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Location:
              </Typography>
              <br />
              <Typography variant="body2">
                {getTrackCarLocationDisplayLabel(record.trackCar) ??
                  "NOT SPOTTED"}
              </Typography>
              {record.carrierType === CarrierType.RAIL &&
                record.trackCar?.trackId && (
                  <Button
                    label="MAP"
                    color="default"
                    onClick={handleTrackClick}
                  >
                    <TrainIcon fontSize="small" />
                  </Button>
                )}
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Account:
            </Typography>
            <br />
            {(() => {
              const isInternal = isInternalAccount(record.account);
              const displayId = isInternal
                ? record.billToAccountId || record.accountId
                : record.accountId;
              const displayName = isInternal
                ? record.accountName
                : accountNameRenderer(record.account);
              return isAuthorized(AclType.ACCOUNTS) ? (
                <Link
                  to={{
                    pathname: `/accounts/${displayId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {displayName}
                  </Typography>
                </Link>
              ) : (
                <Link
                  to={{
                    pathname: `/accounts/${displayId}/show`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {displayName}
                  </Typography>
                </Link>
              );
            })()}
          </Grid>
          {record.billToAccount &&
            record.billToAccountId !== record.accountId && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Billing Account:
                </Typography>
                <br />
                <Link
                  to={{
                    pathname: `/accounts/${record.billToAccountId}${isAuthorized(AclType.ACCOUNTS) ? "" : "/show"}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {accountNameRenderer(record.billToAccount)}
                  </Typography>
                </Link>
              </Grid>
            )}
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              PO Number:
            </Typography>
            <br />
            <TextField source="poNumber" record={record} />
          </Grid>
          <ShipsTogetherAside
            record={record}
            history={history}
            backPath={backPath}
            editPath="/outboundShipments"
          />
          {record.railCarNumber && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                {record.carrierType === CarrierType.RAIL
                  ? "Railcar Number:"
                  : "Trailer Number:"}
              </Typography>
              <br />
              <TextField source="railCarNumber" record={record} />
            </Grid>
          )}
          {record.pickupNumber && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Pickup Number:
              </Typography>
              <br />
              <TextField source="pickupNumber" record={record} />
            </Grid>
          )}
          {record.invoice &&
            invoiceLinkId &&
            isAuthorized(AclType.INVOICES) && (
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Invoice
                </Typography>
                <br />
                <Link
                  to={{
                    pathname: `/invoices/${invoiceLinkId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {record.invoice.invoiceNumber} :{" "}
                    {formatCurrency(getTotalInvoiced(record))}
                  </Typography>
                </Link>
              </Grid>
            )}
          {record.bolNumber && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                BOL Number:
              </Typography>
              <br />
              <TextField source="bolNumber" record={record} />
            </Grid>
          )}
          <PhotoViewer photos={photos} />
        </Grid>
      </CardContent>
    </Card>
  );
};
